/**
 * Formats a duration in milliseconds into a human-readable string.
 *
 * Rules:
 * - null → "—" (em dash, indicating still running)
 * - < 1000ms → "< 1s"
 * - < 60s → seconds only, e.g. "45s"
 * - < 1h → minutes + seconds, e.g. "12m 34s"
 * - >= 1h → hours + minutes + seconds, e.g. "1h 23m 45s"
 * - Zero components are omitted in h/m/s format
 */
export function formatDuration(ms: number | null): string {
  if (ms === null) {
    return '\u2014'; // em dash
  }

  if (ms < 1000) {
    return '< 1s';
  }

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    const parts: string[] = [`${hours}h`];
    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }
    if (seconds > 0) {
      parts.push(`${seconds}s`);
    }
    return parts.join(' ');
  }

  if (minutes > 0) {
    const parts: string[] = [`${minutes}m`];
    if (seconds > 0) {
      parts.push(`${seconds}s`);
    }
    return parts.join(' ');
  }

  return `${seconds}s`;
}

/**
 * Truncates text to a maximum length, appending "..." if truncated.
 *
 * - If text.length <= maxLength → return text unchanged
 * - If text.length > maxLength → return first maxLength chars + "..."
 */
export function truncateText(text: string, maxLength: number = 200): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + '...';
}
